import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { autoScrollStep, gestureSelection } from '../screens/selection.js';

/**
 * THE ONE SELECTION MECHANISM — a checkbox to the left of each row, a drag across those
 * checkboxes, and a shift-click range. It lived inside `items.tsx` from 0.1.83 until it was
 * hoisted here at 0.1.406.
 *
 * ⚠️ IT IS HOISTED BECAUSE THE ALTERNATIVE WAS TRIED AND WAS WRONG. The maintenance queue and
 * the project/character subsections briefly selected by LONG-PRESSING THE ROW ITSELF
 * (0.1.398–0.1.405). It read well, and it fought the one gesture a phone list exists for. The
 * report:
 *
 *   *"when I am scrolling up or down the page, I am scrolling with my finger on top of an item
 *   row or box row. This then causes it to select that row while I am scrolling, even though I
 *   do not want to select that row. It just needs to be exactly the same as the item tab list."*
 *
 * That is not a tuning problem. No long-press delay separates "holding still to read" from
 * "about to scroll", because they are the same gesture until the finger moves. The checkbox
 * settles it by making the SELECT TARGET a different 28px of screen from the scroll surface:
 * `.select-box` carries `touch-action: none`, so the browser hands US the gesture that starts
 * there and keeps scrolling everywhere else. The auto-scroll below then gives scrolling back,
 * under our control, so one drag can still select past the fold.
 *
 * "It needs to be the exact same" was the request, so this is the SAME CODE rather than a second
 * implementation of the same description — four lists that merely agreed today would drift.
 *
 * ⚠️ A LONG-PRESS CAME BACK, ON ONE LIST, BY RULING (0.1.523; maintainer, 1 Oct 2026: "when I long
 * press a reservation row in the reservation tab, it should go into select mode"). It is NOT the
 * 0.1.398 version: `use-long-press.ts` is touch-only and cancelled by any scroll or an 8px move, and
 * it only ARMS the mode — the checkbox stays the way every further row is picked. If the old
 * complaint returns ("it selects while I scroll"), that file is where to look first.
 *
 * What the CALLER still owns: the ordered rows, the list element (for auto-scroll), what a plain
 * row tap does, and what the selection can act on.
 */
export interface CheckboxSelection {
  selected: ReadonlySet<string>;
  /** The raw React setter, so callers can pass a value OR an updater. */
  setSelected: Dispatch<SetStateAction<ReadonlySet<string>>>;
  clear: () => void;
  /** Forget the shift-click anchor — after a select-all, the old anchor means nothing. */
  resetAnchor: () => void;
  /** Toggle one row; `shiftKey` extends from the last plain pick (the desktop range). */
  toggle: (id: string, index: number, shiftKey: boolean) => void;
  /** Spread onto each row's checkbox; `index` is that row's position in `rows`. */
  boxHandlers: (index: number) => {
    onPointerDown: (e: React.PointerEvent<HTMLInputElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLInputElement>) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
  };
  /**
   * True exactly once after a drag, so the click that trails it does not also fire the row's own
   * tap. Call it from the row handler and bail when it returns true.
   */
  swallowClick: () => boolean;
}

/**
 * @param getRows the rows in DRAWN order — the range maths walks this, so it must match the eye.
 *                A GETTER, not an array: the callers that hold their rows in a ref can hand it
 *                over without rebuilding a list every render, and the one that builds a fresh
 *                array can still pass `() => rows`.
 * @param listEl  returns the scrollable list element, or null to scroll the window instead.
 */
export function useCheckboxSelection(
  getRows: () => readonly { id: string }[],
  listEl: () => HTMLElement | null = () => null,
): CheckboxSelection {
  const [selected, setSelectedState] = useState<ReadonlySet<string>>(new Set());
  // Refs so the pointer handlers can stay dependency-free — they are attached to every row, and
  // re-creating them each render is a per-row cost on a fifty-row list.
  const selectedRef = useRef<ReadonlySet<string>>(selected);
  selectedRef.current = selected;
  const rowsRef = useRef<() => readonly { id: string }[]>(getRows);
  rowsRef.current = getRows;

  const lastPicked = useRef<{ index: number; base: ReadonlySet<string> } | null>(null);
  const drag = useRef<{ anchor: number; base: ReadonlySet<string>; x: number; y: number; active: boolean } | null>(
    null,
  );
  const dragFrame = useRef<number | null>(null);
  const suppress = useRef(false);

  const clear = useCallback((): void => {
    lastPicked.current = null;
    setSelectedState(new Set());
  }, []);

  const resetAnchor = useCallback((): void => {
    lastPicked.current = null;
  }, []);

  const toggle = useCallback((id: string, index: number, shiftKey: boolean): void => {
    // SHIFT-CLICK (desktop): the range from the anchor tap to this row, rebuilt each time from
    // the anchor's OWN base — so a second shift-click nearer the anchor shrinks the range instead
    // of accumulating, and a shift-click after DESELECTING mass-deselects (the anchor's starting
    // state sets the direction; `selection.ts`). The anchor does not move: only a plain click
    // plants a new one.
    if (shiftKey && lastPicked.current !== null) {
      const { index: anchor, base } = lastPicked.current;
      setSelectedState(gestureSelection(base, rowsRef.current(), anchor, index));
      return;
    }
    lastPicked.current = { index, base: selectedRef.current };
    setSelectedState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** The row index under a viewport point. The element under a MOVING finger is not the one the
   *  gesture started on, so the drag hit-tests by point rather than trusting the event target. */
  const rowIndexAt = (x: number, y: number): number | null => {
    const row = document.elementFromPoint(x, y)?.closest('[data-row-index]');
    if (row === null || row === undefined) return null;
    const parsed = Number((row as HTMLElement).dataset['rowIndex']);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const applyDrag = (): void => {
    const d = drag.current;
    if (d === null) return;
    const index = rowIndexAt(d.x, d.y);
    if (index === null) return;
    // A follow-up shift-click extends THIS drag's range: same anchor, same base.
    lastPicked.current = { index: d.anchor, base: d.base };
    setSelectedState(gestureSelection(d.base, rowsRef.current(), d.anchor, index));
  };

  const endDrag = (): void => {
    if (drag.current?.active === true) suppress.current = true;
    drag.current = null;
    if (dragFrame.current !== null) cancelAnimationFrame(dragFrame.current);
    dragFrame.current = null;
  };

  const boxHandlers = useCallback(
    (index: number) => ({
      onPointerDown: (e: React.PointerEvent<HTMLInputElement>): void => {
        (e.target as HTMLInputElement).setPointerCapture(e.pointerId);
        // Not `active` yet: a tap must stay a tap — the bubbled click does the toggle.
        drag.current = { anchor: index, base: selectedRef.current, x: e.clientX, y: e.clientY, active: false };
      },
      onPointerMove: (e: React.PointerEvent<HTMLInputElement>): void => {
        const d = drag.current;
        if (d === null) return;
        d.x = e.clientX;
        d.y = e.clientY;
        if (!d.active) {
          // The gesture becomes a DRAG the moment the pointer covers a DIFFERENT row. Until then
          // it is still a tap, which is what lets a slightly wobbly finger tick one box.
          const over = rowIndexAt(d.x, d.y);
          if (over === null || over === d.anchor) return;
          d.active = true;
          const tick = (): void => {
            const live = drag.current;
            if (live === null || !live.active) return;
            const step = autoScrollStep(live.y, window.innerHeight);
            if (step !== 0) {
              const el = listEl();
              // `+ 1` is items.tsx's own `listScrolls` threshold, carried over verbatim: a list
              // one pixel taller than its box is not a scroller, and rounding makes that common.
              if (el !== null && el.scrollHeight > el.clientHeight + 1) el.scrollTop += step;
              else window.scrollBy(0, step);
              applyDrag(); // the page moved under a still finger — recompute
            }
            dragFrame.current = requestAnimationFrame(tick);
          };
          dragFrame.current = requestAnimationFrame(tick);
        }
        applyDrag();
      },
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    }),
    [listEl],
  );

  const swallowClick = useCallback((): boolean => {
    if (!suppress.current) return false;
    suppress.current = false;
    return true;
  }, []);

  return { selected, setSelected: setSelectedState, clear, resetAnchor, toggle, boxHandlers, swallowClick };
}

/**
 * THE BOX ITSELF — a component rather than markup each caller repeats, because three of its
 * details are load-bearing and easy to leave out:
 *
 *  · `stopPropagation` on the click — without it every tick ALSO fires the row's own tap, which
 *    is how selecting a garment used to open it;
 *  · `readOnly` — the click is the toggle path, and this silences React's controlled-input
 *    warning without adding a second one;
 *  · the pointer trio — the drag gesture, which works only because `.select-box` sets
 *    `touch-action: none`.
 */
export function SelectBox({
  id,
  label,
  index,
  pick,
}: {
  id: string;
  /** Names the row, so thirty boxes are not thirty identical "Select" controls. */
  label: string;
  index: number;
  pick: CheckboxSelection;
}): JSX.Element {
  return (
    <input
      type="checkbox"
      className="select-box"
      aria-label={label}
      checked={pick.selected.has(id)}
      readOnly
      onClick={(e) => {
        e.stopPropagation();
        /**
         * ⚠️ THE CLICK THAT TRAILS A DRAG MUST NOT TOGGLE. A gesture over the boxes ends with
         * `pointerup` ON A CHECKBOX, and the browser then fires a click there — which would flip
         * the anchor row straight back off, so a three-row drag reported two. This guard used to
         * live in `items.tsx`'s `onToggle`; it belongs here, with the gesture that arms it, or
         * every new caller has to remember it.
         */
        if (pick.swallowClick()) return;
        pick.toggle(id, index, e.shiftKey);
      }}
      {...pick.boxHandlers(index)}
    />
  );
}
